import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Textarea } from '@/components/ui/textarea';
import {
  Upload, Sparkles, Download, Loader2, CheckCircle, AlertCircle,
  RotateCcw, Monitor, X, ImagePlus, Layers, Video, FolderOpen,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useCredits, CREDIT_OPERATIONS } from '@/hooks/useCredits';
import { useCreditConfirm } from '@/components/credits/CreditConfirmDialog';
import { UpgradeModal } from './subscription';
import { LoginModal } from './LoginModal';
import { ToolShell, StepCard, StepHeader, type ToolStep } from './tool-shell';
import LibraryPicker, { probeVideoUrl, stagedFileName, type StagedLibraryFile } from './library/LibraryPicker';
import { useNavigate } from 'react-router-dom';

type WizardStep = 'upload' | 'background' | 'process' | 'download';

const STEP_ORDER: WizardStep[] = ['upload', 'background', 'process', 'download'];

const STEPS: ToolStep[] = [
  { key: 'upload', label: 'Upload', blurb: 'Drop in the clip you want to cut out.' },
  { key: 'background', label: 'Background', blurb: 'Pick a backdrop or go green-screen.' },
  { key: 'process', label: 'Process', blurb: 'Joe removes the room.' },
  { key: 'download', label: 'Download', blurb: 'Save the finished video.' },
];

interface VideoInfo {
  name: string;
  objectUrl: string;
  s3Key: string;
  s3Url: string;
  duration: number;
  width: number;
  height: number;
  status: 'uploading' | 'converting' | 'uploaded' | 'error';
  uploadProgress?: number;
}

// Small segmented control matching the design's Radio pattern (enhancer.jsx
// line 1082) — pill row with the active option getting a raised surface.
// Local to this file; not yet promoted to a shared primitive.
const SegmentedControl: React.FC<{
  options: { value: string; label: string }[];
  active: string;
  onChange: (v: string) => void;
}> = ({ options, active, onChange }) => (
  <div
    className="flex"
    style={{
      background: 'var(--hj-surface-2)',
      borderRadius: 'var(--hj-r-sm)',
      padding: 3,
      gap: 2,
      border: '1px solid var(--hj-hairline-soft)',
    }}
  >
    {options.map((o) => {
      const isActive = o.value === active;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className="flex-1 transition-colors"
          style={{
            padding: '8px 10px',
            background: isActive ? 'var(--hj-surface-3)' : 'transparent',
            border: 0,
            borderRadius: 'calc(var(--hj-r-sm) - 2px)',
            color: isActive ? 'var(--hj-ink)' : 'var(--hj-ink-3)',
            fontSize: 12.5,
            fontWeight: isActive ? 600 : 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 36,
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

export const BackgroundRemoverTool = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getAuthHeaders, getAuthToken } = useAuth();
  const { isSubscriber, loading: subscriptionLoading } = useSubscription();
  const { checkCredits } = useCredits();
  const { confirmCredit, CreditConfirmDialog } = useCreditConfirm();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(new Set());

  // Upload state
  const [video, setVideo] = useState<VideoInfo | null>(null);

  // Background state
  const bgRemovalJobIdRef = useRef<string | null>(null);
  const bgRemovalIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const renderIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [bgMode, setBgMode] = useState<'replace' | 'greenscreen'>('replace');
  const [maskUrl, setMaskUrl] = useState('');
  const [greenScreenUrl, setGreenScreenUrl] = useState('');
  const [normalizedSourceUrl, setNormalizedSourceUrl] = useState('');
  const [bgReplacementUrl, setBgReplacementUrl] = useState('');
  const [bgReplacementType, setBgReplacementType] = useState<'image' | 'video' | 'preset'>('image');
  const [bgGenPrompt, setBgGenPrompt] = useState('');
  const [bgGenAspectRatio, setBgGenAspectRatio] = useState('');
  const [bgGenType, setBgGenType] = useState<'image' | 'video'>('image');
  const [bgPickerTab, setBgPickerTab] = useState<'saved' | 'generate' | 'upload'>('generate');
  const [bgGenerating, setBgGenerating] = useState(false);
  const [bgUploadingFile, setBgUploadingFile] = useState(false);
  const [userAssets, setUserAssets] = useState<any[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [bgVideoDuration, setBgVideoDuration] = useState<number | null>(null);
  const [bgVideoLoop, setBgVideoLoop] = useState(true);

  // Process state
  const [processStatus, setProcessStatus] = useState<'idle' | 'removing' | 'compositing' | 'completed' | 'failed'>('idle');
  const [processProgress, setProcessProgress] = useState(0);
  const [processMessage, setProcessMessage] = useState('');
  const [processError, setProcessError] = useState('');

  // Download state
  const [finalVideoUrl, setFinalVideoUrl] = useState('');

  // Track paid credit operations so retries don't re-charge
  const [creditsPaidFor, setCreditsPaidFor] = useState<Set<string>>(new Set());

  // Credit confirmation helper
  const isCreditUser = useCallback(() => !!getAuthToken(), [getAuthToken]);

  const requireCreditConfirmation = useCallback(async (
    operationType: string,
    operationName: string,
    onConfirm: () => void
  ) => {
    // Skip credit check if already paid in this session
    if (creditsPaidFor.has(operationType)) { onConfirm(); return; }
    if (!isCreditUser()) { onConfirm(); return; }
    const creditCheck = await checkCredits(operationType);
    if (creditCheck?.isFree) { onConfirm(); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) {
      confirmCredit(operationType as any, operationName, () => {
        setCreditsPaidFor(prev => new Set([...prev, operationType]));
        onConfirm();
      });
    } else {
      toast({ title: 'Not enough video tokens', description: 'Please purchase more video tokens to continue.', variant: 'destructive' });
    }
  }, [isCreditUser, checkCredits, isSubscriber, subscriptionLoading, confirmCredit, toast, creditsPaidFor]);

  // ===== NAVIGATION =====
  const stepIndex = STEP_ORDER.indexOf(currentStep);

  const clearTimers = () => {
    if (bgRemovalIntervalRef.current) { clearInterval(bgRemovalIntervalRef.current); bgRemovalIntervalRef.current = null; }
    if (renderIntervalRef.current) { clearInterval(renderIntervalRef.current); renderIntervalRef.current = null; }
    bgRemovalJobIdRef.current = null;
  };

  useEffect(() => () => clearTimers(), []);

  const resetFromProcess = () => {
    clearTimers();
    setProcessStatus('idle');
    setProcessProgress(0);
    setProcessMessage('');
    setProcessError('');
    setFinalVideoUrl('');
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.delete('process');
      next.delete('download');
      return next;
    });
  };

  const resetFromBackground = () => {
    // Preserve Replicate results (maskUrl, greenScreenUrl, normalizedSourceUrl)
    // so the user doesn't have to re-run the expensive background removal job.
    // Only reset the background replacement choice + downstream state.
    resetFromProcess();
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.delete('background');
      return next;
    });
  };

  const resetFromUpload = () => {
    resetFromBackground();
    // Source video changed — invalidate BG removal results (mask is tied to the source)
    setMaskUrl('');
    setGreenScreenUrl('');
    setNormalizedSourceUrl('');
    setBgReplacementUrl('');
    setBgReplacementType('image');
  };

  const resetAll = () => {
    if (video) URL.revokeObjectURL(video.objectUrl);
    setVideo(null);
    resetFromUpload();
    setBgMode('replace');
    setBgGenPrompt('');
    setCompletedSteps(new Set());
    setCurrentStep('upload');
  };

  const processInFlight = processStatus === 'removing' || processStatus === 'compositing';

  const goToStep = (step: WizardStep) => {
    const targetIndex = STEP_ORDER.indexOf(step);
    if (targetIndex > stepIndex && !completedSteps.has(STEP_ORDER[targetIndex - 1])) return;
    if (processInFlight) return; // don't navigate mid-pipeline
    if (targetIndex < stepIndex) {
      if (step === 'upload') resetFromBackground();
      else if (step === 'background') resetFromProcess();
    }
    setCurrentStep(step);
  };

  const advanceStep = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(STEP_ORDER[nextIndex]);
    }
  };

  // ===== OUTPUT SIZING (single-source versions of the enhancer helpers) =====
  const getSourceDimensions = (): { width: number; height: number } => {
    if (video?.width && video?.height) return { width: video.width, height: video.height };
    return { width: 1920, height: 1080 };
  };

  const getAspectRatioCategory = (): '16:9' | '9:16' | '1:1' | '4:3' | '3:4' | 'custom' => {
    const { width, height } = getSourceDimensions();
    const ratio = width / height;
    if (Math.abs(ratio - 1) < 0.05) return '1:1';
    if (Math.abs(ratio - 16 / 9) < 0.1) return '16:9';
    if (Math.abs(ratio - 9 / 16) < 0.1) return '9:16';
    if (Math.abs(ratio - 4 / 3) < 0.1) return '4:3';
    if (Math.abs(ratio - 3 / 4) < 0.1) return '3:4';
    return 'custom';
  };

  // Get Shotstack output config that preserves source aspect ratio
  const getShotstackOutputConfig = () => {
    const ar = getAspectRatioCategory();
    const { width, height } = getSourceDimensions();
    // For standard 16:9, use Shotstack's built-in 'hd' resolution
    if (ar === '16:9') return { format: 'mp4', resolution: 'hd', fps: 30, quality: 'high' };
    // For all other aspect ratios, preserve source dimensions exactly when they
    // already fit inside a 1920×1080 box. Only scale down when source is bigger.
    let outW = width;
    let outH = height;
    const maxW = 1920;
    const maxH = 1080;
    if (outW > maxW || outH > maxH) {
      const scale = Math.min(maxW / outW, maxH / outH);
      outW = Math.round(outW * scale);
      outH = Math.round(outH * scale);
    }
    outW = outW % 2 === 0 ? outW : outW + 1;
    outH = outH % 2 === 0 ? outH : outH + 1;
    return { format: 'mp4', size: { width: outW, height: outH }, fps: 30, quality: 'high' };
  };

  // ===== STEP 1: UPLOAD =====
  const uploadFileToS3 = async (file: File) => {
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fileName: file.name, fileType: file.type || 'video/mp4', ephemeral: true }),
      });
      if (!res.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, key, downloadUrl } = await res.json();

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setVideo(prev => prev ? { ...prev, uploadProgress: pct } : prev);
          }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      return { key, downloadUrl };
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Upload failed', description: `${file.name}: ${err.message}`, variant: 'destructive' });
      return null;
    }
  };

  // MOV → MP4 conversion via AWS MediaConvert (handles HDR→SDR tone mapping)
  const convertMovClip = async (s3Key: string) => {
    try {
      const res = await fetch('/api/convert-mov', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key }),
      });
      if (!res.ok) throw new Error('Failed to start conversion');
      const { jobId } = await res.json();
      pollConversion(jobId);
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Conversion failed', description: err.message, variant: 'destructive' });
    }
  };

  const pollConversion = (jobId: string) => {
    const poll = async () => {
      try {
        const res = await fetch(`/api/convert-mov/status/${jobId}`);
        if (!res.ok) throw new Error('Failed to check conversion status');
        const data = await res.json();

        if (data.status === 'completed') {
          // Load metadata from the converted MP4 (Chrome can read H.264)
          const tempVideo = document.createElement('video');
          tempVideo.preload = 'metadata';
          tempVideo.src = data.downloadUrl;
          let metadataHandled = false;
          const updateAsUploaded = (withMetadata: boolean) => {
            if (metadataHandled) return;
            metadataHandled = true;
            setVideo(prev => prev ? {
              ...prev,
              s3Key: data.outputKey,
              s3Url: data.downloadUrl,
              objectUrl: `/api/s3/hey-joe-video-rotator/${data.outputKey}`,
              ...(withMetadata ? {
                duration: tempVideo.duration,
                width: tempVideo.videoWidth,
                height: tempVideo.videoHeight,
              } : {}),
              status: 'uploaded' as const,
            } : prev);
          };
          tempVideo.onloadedmetadata = () => updateAsUploaded(true);
          tempVideo.onerror = () => updateAsUploaded(false);
          // Fallback: if neither event fires within 10s, update anyway
          setTimeout(() => updateAsUploaded(false), 10000);
        } else if (data.status === 'failed') {
          setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
          toast({ title: 'MOV conversion failed', description: data.error, variant: 'destructive' });
        } else {
          // Still processing — poll again in 5 seconds
          setTimeout(poll, 5000);
        }
      } catch (err: any) {
        setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
        toast({ title: 'Conversion error', description: err.message, variant: 'destructive' });
      }
    };
    poll();
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    // Require login + subscription before allowing uploads
    if (!getAuthToken()) {
      setShowLoginModal(true);
      return;
    }
    if (!subscriptionLoading && !isSubscriber) {
      setShowUpgradeModal(true);
      return;
    }

    const file = acceptedFiles.find(f =>
      f.type.startsWith('video/') || ['.mp4', '.mov', '.webm', '.mkv', '.avi'].some(ext => f.name.toLowerCase().endsWith(ext))
    );
    if (!file) {
      toast({ title: 'Invalid file', description: 'Please select a video file.', variant: 'destructive' });
      return;
    }

    // New source — clear any previous video + downstream results
    if (video) URL.revokeObjectURL(video.objectUrl);
    resetFromUpload();

    const objectUrl = URL.createObjectURL(file);
    setVideo({
      name: file.name,
      objectUrl,
      s3Key: '',
      s3Url: '',
      duration: 0,
      width: 0,
      height: 0,
      status: 'uploading',
      uploadProgress: 0,
    });

    // Capture duration / dimensions from metadata (needed for the
    // Shotstack composite length + output size)
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.src = objectUrl;
    tempVideo.onloadedmetadata = () => {
      setVideo(prev => prev ? {
        ...prev,
        duration: tempVideo.duration,
        width: tempVideo.videoWidth,
        height: tempVideo.videoHeight,
      } : prev);
    };

    const result = await uploadFileToS3(file);
    if (result) {
      const needsConversion = !file.name.toLowerCase().endsWith('.mp4');
      if (needsConversion) {
        // Non-MP4 files (MOV, WebM, AVI, MKV) need MediaConvert conversion to normalized MP4
        setVideo(prev => prev ? { ...prev, s3Key: result.key, s3Url: result.downloadUrl, status: 'converting' as const } : prev);
        convertMovClip(result.key);
      } else {
        setVideo(prev => prev ? { ...prev, s3Key: result.key, s3Url: result.downloadUrl, status: 'uploaded' as const } : prev);
      }
    }
  }, [getAuthToken, getAuthHeaders, isSubscriber, subscriptionLoading, toast, video]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'] },
    maxFiles: 1,
  });

  // "Choose from your library" — a staged library file slots in exactly like a
  // completed upload (the staged key already lives in the ephemeral tool bucket).
  const openLibraryPicker = () => {
    if (!getAuthToken()) { setShowLoginModal(true); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    setShowLibraryPicker(true);
  };

  const handleLibraryPick = async (f: StagedLibraryFile) => {
    const fileName = stagedFileName(f);
    // New source — clear any previous video + downstream results (mirrors onDrop)
    if (video) URL.revokeObjectURL(video.objectUrl);
    resetFromUpload();

    const base: VideoInfo = {
      name: f.name,
      objectUrl: f.downloadUrl,
      s3Key: f.key,
      s3Url: f.downloadUrl,
      duration: 0,
      width: 0,
      height: 0,
      status: 'uploaded',
    };
    if (!fileName.toLowerCase().endsWith('.mp4')) {
      // Non-MP4 library files go through the same MediaConvert path as uploads
      setVideo({ ...base, status: 'converting' });
      convertMovClip(f.key);
      return;
    }
    setVideo(base);
    try {
      const meta = await probeVideoUrl(f.downloadUrl);
      setVideo(prev => prev ? { ...prev, duration: meta.duration, width: meta.width, height: meta.height } : prev);
    } catch { /* output sizing falls back gracefully */ }
  };

  const videoReady = !!video && video.status === 'uploaded';

  // ===== STEP 2: BACKGROUND (advanced picker) =====
  // Fetch user's generated assets when entering the background step
  useEffect(() => {
    if (currentStep === 'background') {
      const fetchUserAssets = async () => {
        setLoadingAssets(true);
        try {
          const authToken = getAuthToken();
          if (!authToken) { setLoadingAssets(false); return; }
          const res = await fetch('/api/user/generated-assets', {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (res.ok) {
            const data = await res.json();
            setUserAssets(data.assets || []);
          }
        } catch { /* ignore */ }
        finally { setLoadingAssets(false); }
      };
      fetchUserAssets();
    }
  }, [currentStep, getAuthToken]);

  // Detect video background duration when selected
  useEffect(() => {
    if (!bgReplacementUrl || bgReplacementType !== 'video') {
      setBgVideoDuration(null);
      return;
    }
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.crossOrigin = 'anonymous';
    const onLoaded = () => {
      if (tempVideo.duration && isFinite(tempVideo.duration)) {
        setBgVideoDuration(tempVideo.duration);
      }
      tempVideo.removeEventListener('loadedmetadata', onLoaded);
      tempVideo.src = '';
    };
    tempVideo.addEventListener('loadedmetadata', onLoaded);
    tempVideo.src = bgReplacementUrl;
    return () => {
      tempVideo.removeEventListener('loadedmetadata', onLoaded);
      tempVideo.src = '';
    };
  }, [bgReplacementUrl, bgReplacementType]);

  const generateBackground = async () => {
    if (!bgGenPrompt.trim()) {
      toast({ title: 'Enter a prompt', description: 'Describe the background you want.', variant: 'destructive' });
      return;
    }
    const operationType = bgGenType === 'image' ? CREDIT_OPERATIONS.IMAGE_GENERATION : CREDIT_OPERATIONS.VIDEO_GENERATION;
    const operationName = bgGenType === 'image' ? 'Image Generation' : 'Video Generation';

    requireCreditConfirmation(operationType, operationName, async () => {
      setBgGenerating(true);
      try {
        const authToken = getAuthToken();
        const endpoint = bgGenType === 'image' ? '/api/generate-image' : '/api/generate-video';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
          body: JSON.stringify({ prompt: bgGenPrompt, aspectRatio: bgGenAspectRatio }),
        });
        if (!res.ok) throw new Error('Generation failed');
        const { jobId } = await res.json();
        toast({ title: 'Generating background', description: 'This may take a moment...' });

        const maxAttempts = bgGenType === 'video' ? 180 : 60;
        let attempts = 0;
        const interval = setInterval(async () => {
          attempts++;
          try {
            const statusRes = await fetch(`/api/generate-asset/status/${jobId}`);
            const statusData = await statusRes.json();
            if (statusData.status === 'completed' && statusData.assetUrl) {
              clearInterval(interval);
              const assetUrl = statusData.assetUrl.startsWith('/api/s3/')
                ? `${window.location.origin}${statusData.assetUrl}`
                : statusData.assetUrl;
              setBgReplacementUrl(assetUrl);
              setBgReplacementType(bgGenType);
              setBgGenerating(false);
              // Add to user assets list immediately so it shows in "Saved"
              if (statusData.s3Key) {
                setUserAssets(prev => [{
                  type: bgGenType,
                  s3Key: statusData.s3Key,
                  prompt: bgGenPrompt,
                  url: statusData.assetUrl,
                  createdAt: new Date().toISOString()
                }, ...prev]);
              }
              toast({ title: 'Background generated' });
            } else if (statusData.status === 'failed' || attempts >= maxAttempts) {
              clearInterval(interval);
              setBgGenerating(false);
              toast({ title: statusData.status === 'failed' ? 'Generation failed' : 'Generation timed out', variant: 'destructive' });
            }
          } catch { if (attempts >= maxAttempts) { clearInterval(interval); setBgGenerating(false); } }
        }, 5000);
      } catch (err: any) {
        setBgGenerating(false);
        toast({ title: 'Generation failed', description: err.message, variant: 'destructive' });
      }
    });
  };

  const handleBgFileUpload = async (file: File) => {
    setBgUploadingFile(true);
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, fileType: file.type }),
      });
      const { uploadUrl, downloadUrl } = await res.json();
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setBgReplacementUrl(downloadUrl);
      setBgReplacementType(file.type.startsWith('video/') ? 'video' : 'image');
      toast({ title: 'Background uploaded' });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setBgUploadingFile(false);
    }
  };

  const bgFileDropzone = useDropzone({
    onDrop: (files) => files[0] && handleBgFileUpload(files[0]),
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'], 'video/*': ['.mp4', '.mov', '.webm'] },
    maxFiles: 1,
    disabled: bgUploadingFile,
  });

  // ===== STEP 3: PROCESS =====
  const failProcess = (msg: string) => {
    setProcessStatus('failed');
    setProcessError(msg);
    toast({ title: 'Processing failed', description: msg, variant: 'destructive' });
  };

  const finishProcess = (url: string) => {
    setFinalVideoUrl(url);
    setProcessStatus('completed');
    setProcessProgress(100);
    setCompletedSteps(prev => new Set([...prev, 'background', 'process']));
    setCurrentStep('download');
  };

  // Build the luma-mask compositing JSON: mask track (luma + background clips)
  // over the foreground track. Adapted from the Audition Enhancer's
  // buildShotstackJson — Path A (replace) only; green-screen export skips
  // Shotstack entirely (Replicate's output IS the final video).
  const buildCompositeJson = (maskUrlArg: string, normalizedSourceUrlArg: string): any => {
    const sourceVideoUrl = video?.s3Url || '';
    // Use 30fps normalized source when compositing with mask to prevent fps drift
    const compositeSourceUrl = normalizedSourceUrlArg || sourceVideoUrl;
    const effectiveDuration = video?.duration || 0;
    const tracks: any[] = [];

    // Track 0 (front): [luma mask, background] - mask cuts out person shape from bg
    // Track 1 (back): [foreground video] - person shows through
    // Both mask and source must be 30fps (backend normalizes source before Replicate)
    const foregroundClip: any = { asset: { type: 'video', src: compositeSourceUrl, volume: 1 }, start: 0, length: effectiveDuration };
    const maskClip: any = { asset: { type: 'luma', src: maskUrlArg }, start: 0, length: effectiveDuration };

    // Background clip(s) - handles presets, video looping, and images
    const bgClips: any[] = [];
    if (bgReplacementType === 'preset' && bgReplacementUrl.startsWith('solid:')) {
      // Solid color backgrounds use image assets (not HTML - HTML breaks luma compositing)
      const solidImageMap: Record<string, string> = {
        '#808080': '/stock-backgrounds/solid-gray.png',
        '#ffffff': '/stock-backgrounds/solid-white.png',
        '#0044cc': '/stock-backgrounds/solid-blue.png',
      };
      const color = bgReplacementUrl.replace('solid:', '');
      const imagePath = solidImageMap[color] || solidImageMap['#808080'];
      bgClips.push({
        asset: { type: 'image', src: `${window.location.origin}${imagePath}` },
        start: 0, length: effectiveDuration, fit: 'cover',
      });
    } else {
      const needsLoop = bgReplacementType === 'video' && bgVideoLoop && bgVideoDuration && bgVideoDuration < effectiveDuration;
      if (needsLoop && bgVideoDuration) {
        let remaining = effectiveDuration;
        let offset = 0;
        while (remaining > 0) {
          const clipLen = Math.min(bgVideoDuration, remaining);
          bgClips.push({ asset: { type: 'video', src: bgReplacementUrl, volume: 0 }, start: offset, length: clipLen, fit: 'cover' });
          offset += clipLen;
          remaining -= clipLen;
        }
      } else {
        const bgClip: any = { asset: { type: bgReplacementType === 'video' ? 'video' : 'image', src: bgReplacementUrl }, start: 0, length: effectiveDuration, fit: 'cover' };
        if (bgReplacementType === 'video') bgClip.asset.volume = 0;
        bgClips.push(bgClip);
      }
    }

    tracks.push({ clips: [maskClip, ...bgClips] });
    tracks.push({ clips: [foregroundClip] });

    const json = { timeline: { tracks }, output: getShotstackOutputConfig() };
    console.log('[BackgroundRemover] Shotstack JSON:', JSON.stringify(json, null, 2));
    return json;
  };

  const pollRenderStatus = (id: string) => {
    if (renderIntervalRef.current) clearInterval(renderIntervalRef.current);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`/api/shotstack/render-status/${id}`);
        const data = await res.json();
        const doneUrl = data.videoUrl || data.url;
        if (data.status === 'done' && doneUrl) {
          clearInterval(interval);
          renderIntervalRef.current = null;
          finishProcess(doneUrl);
          toast({ title: 'Background replaced', description: 'Your video is ready to download.' });
        } else if (data.status === 'failed') {
          clearInterval(interval);
          renderIntervalRef.current = null;
          const errMsg = data.errorDetails?.message || data.error || data.message || 'Rendering failed.';
          console.error('[BackgroundRemover] Render failed:', data);
          failProcess(errMsg);
        } else {
          if (data.status === 'fetching') setProcessProgress(10);
          else if (data.status === 'queued') setProcessProgress(20);
          else if (data.status === 'rendering') setProcessProgress(prev => Math.min(90, prev + 5));
          else if (data.status === 'saving') setProcessProgress(95);
        }
        if (attempts >= 360) {
          clearInterval(interval);
          renderIntervalRef.current = null;
          failProcess('Render timed out');
        }
      } catch { if (attempts >= 360) { clearInterval(interval); renderIntervalRef.current = null; failProcess('Render timed out'); } }
    }, 5000);
    renderIntervalRef.current = interval;
  };

  const startComposite = async (maskUrlArg: string, normalizedSourceUrlArg: string) => {
    setProcessStatus('compositing');
    setProcessProgress(0);
    setProcessMessage('Compositing your new background...');
    try {
      const authToken = getAuthToken();
      const json = buildCompositeJson(maskUrlArg, normalizedSourceUrlArg);
      const res = await fetch('/api/shotstack/render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({ json }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.details || 'Render submission failed');
      }
      const renderData = await res.json();
      toast({ title: 'Compositing video...' });
      pollRenderStatus(renderData.id);
    } catch (err: any) {
      failProcess(err.message);
    }
  };

  const pollBgRemoval = (jobId: string) => {
    // Clear any existing interval before starting a new one
    if (bgRemovalIntervalRef.current) clearInterval(bgRemovalIntervalRef.current);

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/remove-video-background/status/${jobId}`);
        const data = await res.json();
        // Update progress from backend
        if (data.progress !== undefined) setProcessProgress(data.progress);
        if (data.message) setProcessMessage(data.message);
        if (data.status === 'completed') {
          clearInterval(interval);
          bgRemovalIntervalRef.current = null;
          bgRemovalJobIdRef.current = null;
          const resolvedUrl = data.videoUrl?.startsWith('/api/s3/') ? `${window.location.origin}${data.videoUrl}` : data.videoUrl;

          if (bgMode === 'greenscreen') {
            // Green screen output: Replicate produced a green screen video with
            // audio merged — that IS the final result. No Shotstack needed.
            setGreenScreenUrl(resolvedUrl);
            finishProcess(resolvedUrl);
            toast({ title: 'Green screen video ready', description: 'Your video with green background is ready to download.' });
          } else {
            // Foreground mask output: got a luma matte for compositing
            setMaskUrl(resolvedUrl);
            // Store 30fps normalized source URL if available (prevents mask/source fps drift)
            let normUrl = '';
            if (data.normalizedSourceUrl) {
              normUrl = data.normalizedSourceUrl.startsWith('/api/s3/')
                ? `${window.location.origin}${data.normalizedSourceUrl}`
                : data.normalizedSourceUrl;
              setNormalizedSourceUrl(normUrl);
            }
            // Composite via Shotstack — confirm render credit first
            requireCreditConfirmation(CREDIT_OPERATIONS.SHOTSTACK_RENDER, 'Background Composite', () => {
              startComposite(resolvedUrl, normUrl);
            });
          }
        } else if (data.status === 'failed' || data.status === 'expired') {
          clearInterval(interval);
          bgRemovalIntervalRef.current = null;
          bgRemovalJobIdRef.current = null;
          failProcess(data.error || 'Background removal failed.');
        }
      } catch { /* keep polling */ }
    }, 5000);
    bgRemovalIntervalRef.current = interval;
    setTimeout(() => { clearInterval(interval); if (bgRemovalIntervalRef.current === interval) bgRemovalIntervalRef.current = null; }, 3600000);
  };

  const startBgRemoval = async () => {
    if (!video) return;

    // Use 'green-screen' output type when user wants green screen directly from
    // Replicate — avoids the Shotstack luma-composite approach which produces
    // green line artifacts.
    const outputType = bgMode === 'greenscreen' ? 'green-screen' : 'foreground-mask';

    setProcessStatus('removing');
    setProcessProgress(0);
    setProcessError('');
    setProcessMessage('Starting background removal...');
    try {
      const authToken = getAuthToken();
      const res = await fetch('/api/remove-video-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ videoUrl: video.s3Url, videoKey: video.s3Key, outputType }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.details || 'Background removal failed');
      }

      const { jobId } = await res.json();
      bgRemovalJobIdRef.current = jobId;
      toast({ title: 'Background removal started', description: 'This may take a few minutes...' });
      pollBgRemoval(jobId);
    } catch (err: any) {
      failProcess(err.message);
    }
  };

  const runPipeline = () => {
    setProcessError('');
    // Re-use existing Replicate results when retrying / re-compositing —
    // the mask is tied to the source video and doesn't need re-running.
    if (bgMode === 'greenscreen' && greenScreenUrl) {
      finishProcess(greenScreenUrl);
      return;
    }
    if (bgMode === 'replace' && maskUrl) {
      requireCreditConfirmation(CREDIT_OPERATIONS.SHOTSTACK_RENDER, 'Background Composite', () => {
        startComposite(maskUrl, normalizedSourceUrl);
      });
      return;
    }
    startBgRemoval();
  };

  const handleBackgroundAdvance = () => {
    if (bgMode === 'replace' && !bgReplacementUrl) return;
    requireCreditConfirmation(CREDIT_OPERATIONS.BACKGROUND_REMOVAL, 'Background Removal', () => {
      setCompletedSteps(prev => new Set([...prev, 'background']));
      setCurrentStep('process');
      runPipeline();
    });
  };

  // Resume polling when page becomes visible again (iPad/Safari suspends timers in background)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && bgRemovalJobIdRef.current && bgRemovalIntervalRef.current) {
        console.log('[BackgroundRemover] Page visible again — restarting bg removal poll');
        pollBgRemoval(bgRemovalJobIdRef.current);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // ===== STEP 4: DOWNLOAD =====
  const getDownloadFileName = () => {
    const baseName = video ? video.name.replace(/\.[^.]+$/, '') : 'video';
    return bgMode === 'greenscreen' ? `green_screen_${baseName}.mp4` : `background_removed_${baseName}.mp4`;
  };

  const handleDownload = async () => {
    const fileName = getDownloadFileName();
    const isIOS = /iPhone|iPad/i.test(navigator.userAgent);

    // iOS: use Web Share API so user gets "Save Video" option for camera roll
    if (isIOS && navigator.share) {
      try {
        // Fetch as blob and share as file — this gives "Save Video" in share sheet
        const resp = await fetch(finalVideoUrl);
        if (resp.ok) {
          const blob = await resp.blob();
          const file = new File([blob], fileName, { type: 'video/mp4' });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: fileName });
            return;
          }
        }
      } catch {
        // Share cancelled or failed — fall through to URL-based share
      }

      // Fallback: share the URL (still gives Save Video on iOS 16+)
      try {
        await navigator.share({ url: finalVideoUrl, title: fileName });
        return;
      } catch {
        // User cancelled — fall through to direct download
      }
    }

    // Determine the fetch URL — proxy URLs get download=1 for Content-Disposition
    let fetchUrl = finalVideoUrl;
    if (finalVideoUrl.includes('/api/s3/')) {
      const separator = finalVideoUrl.includes('?') ? '&' : '?';
      fetchUrl = `${finalVideoUrl}${separator}download=1&filename=${encodeURIComponent(fileName)}`;
    }

    // Desktop / Android: fetch blob and trigger download
    try {
      const resp = await fetch(fetchUrl);
      if (!resp.ok) throw new Error(`${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    } catch {
      // Fetch failed (CORS or 404) — fall through to direct navigation
    }

    // Fallback: direct navigation
    if (finalVideoUrl.includes('/api/s3/')) {
      window.location.href = fetchUrl;
    } else {
      window.open(finalVideoUrl, '_blank');
    }
  };

  // ===== STEP RENDERERS =====
  const renderUploadStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 01 · Upload"
        title="Drop in your clip."
        subtitle="One video at a time. MP4 plays straight through; MOV / WebM / MKV get converted automatically."
      />

      {/* Drop card */}
      <div
        {...getRootProps()}
        className="flex flex-col justify-between cursor-pointer transition-colors"
        style={{
          padding: '18px 20px',
          minHeight: 140,
          borderRadius: 'var(--hj-r-md)',
          border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
          background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
          gap: 14,
        }}
      >
        <input {...getInputProps()} />
        <div className="flex items-center justify-between gap-3">
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 40,
              height: 40,
              borderRadius: 'var(--hj-r-sm)',
              background: 'var(--hj-surface-3)',
              color: 'var(--hj-ink-2)',
            }}
          >
            <Upload size={18} strokeWidth={1.8} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--hj-ink)', marginBottom: 2 }}>
            Drop or browse a clip
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.4 }}>
            MP4 · MOV · WebM · MKV — one video per run.
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={openLibraryPicker}
        className="inline-flex items-center gap-2"
        style={{
          marginTop: 10,
          padding: '8px 12px',
          borderRadius: 'var(--hj-r-sm)',
          border: '1px solid var(--hj-hairline)',
          background: 'transparent',
          color: 'var(--hj-ink-2)',
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <FolderOpen size={14} strokeWidth={1.8} />
        Choose from your library
      </button>

      {video && (
        <div style={{ marginTop: 18 }}>
          <div className="mono-label" style={{ marginBottom: 10 }}>Your clip</div>
          <div
            className="flex items-center"
            style={{
              gap: 14,
              padding: '10px 12px',
              borderRadius: 'var(--hj-r-sm)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
            }}
          >
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 76,
                height: 44,
                borderRadius: 4,
                background: 'oklch(0.12 0.005 70)',
                color: 'var(--hj-ink-4)',
              }}
            >
              <Video className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate" style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--hj-ink)' }}>
                {video.name}
              </div>
              <div style={{ fontSize: 12, color: 'var(--hj-ink-3)', marginTop: 2 }}>
                {video.status === 'uploading' && `Uploading… ${video.uploadProgress ?? 0}%`}
                {video.status === 'converting' && 'Converting to MP4…'}
                {video.status === 'uploaded' && (
                  video.duration > 0
                    ? `${Math.round(video.duration)}s · ${video.width}×${video.height}`
                    : 'Ready'
                )}
                {video.status === 'error' && 'Upload failed — drop the file again'}
              </div>
            </div>
            <div className="flex-shrink-0">
              {(video.status === 'uploading' || video.status === 'converting') && (
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
              )}
              {video.status === 'uploaded' && (
                <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-green)' }} />
              )}
              {video.status === 'error' && (
                <AlertCircle className="w-4 h-4" style={{ color: 'hsl(var(--destructive))' }} />
              )}
            </div>
          </div>

          {/* Upload progress bar */}
          {video.status === 'uploading' && (
            <div
              style={{
                height: 6,
                borderRadius: 999,
                background: 'var(--hj-surface-3)',
                overflow: 'hidden',
                marginTop: 10,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${video.uploadProgress ?? 0}%`,
                  background: 'var(--hj-blue)',
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
          )}

          {/* Preview */}
          {video.status === 'uploaded' && (
            <div
              className="aspect-video overflow-hidden"
              style={{
                background: 'oklch(0.08 0.005 70)',
                borderRadius: 'var(--hj-r-md)',
                border: '1px solid var(--hj-hairline)',
                marginTop: 14,
              }}
            >
              <video
                src={video.objectUrl}
                className="w-full h-full object-contain"
                controls
                preload="metadata"
                playsInline
              />
            </div>
          )}
        </div>
      )}
    </StepCard>
  );

  const renderBackgroundStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 02 · Background"
        title="Drop the room."
        subtitle="Replace with something clean, or export with a green-screen matte. Subject lock is automatic."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" style={{ marginBottom: 22 }}>
        {([
          { k: 'replace',     t: 'Replace',       sub: 'Drop in a clean backdrop',  Icon: Monitor },
          { k: 'greenscreen', t: 'Green-screen',  sub: 'Export with green matte',   Icon: Layers  },
        ] as const).map((o) => {
          const active = bgMode === o.k;
          return (
            <button
              key={o.k}
              type="button"
              onClick={() => setBgMode(o.k)}
              className="flex flex-col items-start gap-1.5 text-left transition-colors"
              style={{
                padding: 14,
                borderRadius: 'var(--hj-r-sm)',
                background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                color: 'var(--hj-ink)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                minHeight: 92,
              }}
            >
              <o.Icon className="w-5 h-5" style={{ color: active ? 'var(--hj-blue)' : 'var(--hj-ink-3)' }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>{o.t}</div>
              <div style={{ fontSize: 12, color: 'var(--hj-ink-2)' }}>{o.sub}</div>
            </button>
          );
        })}
      </div>

      {bgMode === 'greenscreen' && (
        <div
          style={{
            padding: 14,
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
            You'll get your video back with a solid green background (audio preserved) —
            ready to key out in any editor.
          </p>
        </div>
      )}

      {bgMode === 'replace' && (
        <div className="flex flex-col" style={{ gap: 18 }}>
          {/* Solid colors */}
          <section>
            <div className="mono-label" style={{ marginBottom: 10 }}>Solid colors</div>
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { key: 'solid:#808080', label: 'Gray',        style: { background: '#808080' } },
                { key: 'solid:#ffffff', label: 'White',       style: { background: '#ffffff' } },
                { key: 'solid:#0044cc', label: 'Blue screen', style: { background: '#0044cc' } },
              ].map((preset) => {
                const active = bgReplacementUrl === preset.key;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => { setBgReplacementUrl(preset.key); setBgReplacementType('preset'); }}
                    className="relative aspect-video overflow-hidden flex flex-col"
                    style={{
                      padding: 6,
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'var(--hj-surface-2)',
                      border: `1.5px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div
                      className="flex-1"
                      style={{ ...preset.style, borderRadius: 4 }}
                    />
                    <div className="mono-label" style={{ marginTop: 6, fontSize: 9 }}>{preset.label}</div>
                    {active && (
                      <div
                        className="absolute flex items-center justify-center"
                        style={{
                          top: 8,
                          right: 8,
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          background: 'var(--hj-blue)',
                          color: 'oklch(0.18 0.005 70)',
                        }}
                      >
                        <CheckCircle className="w-3 h-3" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Stock backgrounds */}
          <section>
            <div className="mono-label" style={{ marginBottom: 10 }}>Stock backdrops</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {[
                { key: 'stock:studio-gray-soft',  label: 'Gray · Soft',   src: '/stock-backgrounds/studio-gray-soft.png' },
                { key: 'stock:studio-gray-warm',  label: 'Gray · Warm',   src: '/stock-backgrounds/studio-gray-warm.png' },
                { key: 'stock:studio-white-soft', label: 'White · Soft',  src: '/stock-backgrounds/studio-white-soft.png' },
                { key: 'stock:studio-white-warm', label: 'White · Warm',  src: '/stock-backgrounds/studio-white-warm.png' },
                { key: 'stock:studio-blue-soft',  label: 'Blue · Soft',   src: '/stock-backgrounds/studio-blue-soft.png' },
                { key: 'stock:studio-blue-deep',  label: 'Blue · Deep',   src: '/stock-backgrounds/studio-blue-deep.png' },
                { key: 'stock:daylight-loft', label: 'Daylight loft',   src: '/stock-backgrounds/daylight-loft.png' },
                { key: 'stock:loft-bg',       label: 'Industrial loft', src: '/stock-backgrounds/loft-bg.png' },
                { key: 'stock:library',       label: 'Library',         src: '/stock-backgrounds/library.png' },
                { key: 'stock:city-street',   label: 'City street',     src: '/stock-backgrounds/city-street.png' },
              ].map((stock) => {
                const url = `${window.location.origin}${stock.src}`;
                const active = bgReplacementUrl === url;
                return (
                  <button
                    key={stock.key}
                    type="button"
                    onClick={() => { setBgReplacementUrl(url); setBgReplacementType('image'); }}
                    className="relative aspect-video overflow-hidden flex flex-col"
                    style={{
                      padding: 6,
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'var(--hj-surface-2)',
                      border: `1.5px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <img
                      src={stock.src}
                      alt={stock.label}
                      className="flex-1 w-full object-cover"
                      style={{ borderRadius: 4 }}
                    />
                    <div className="mono-label" style={{ marginTop: 6, fontSize: 9 }}>{stock.label}</div>
                    {active && (
                      <div
                        className="absolute flex items-center justify-center"
                        style={{
                          top: 8,
                          right: 8,
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          background: 'var(--hj-blue)',
                          color: 'oklch(0.18 0.005 70)',
                        }}
                      >
                        <CheckCircle className="w-3 h-3" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Custom — tabs (Saved / Generate AI / Upload). Underline-active
              treatment matching the enhancer's FilterStep. */}
          <section>
            <div
              className="flex"
              style={{ borderBottom: '1px solid var(--hj-hairline-soft)', marginBottom: 14 }}
            >
              {(
                [
                  ...(userAssets.length > 0
                    ? [{ k: 'saved' as const, label: `Saved (${userAssets.length})`, Icon: Layers }]
                    : []),
                  { k: 'generate' as const, label: 'Generate AI', Icon: Sparkles },
                  { k: 'upload' as const,   label: 'Upload',      Icon: Upload },
                ]
              ).map((t) => {
                const active = bgPickerTab === t.k;
                return (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setBgPickerTab(t.k)}
                    className="inline-flex items-center gap-1.5 transition-colors"
                    style={{
                      padding: '10px 14px',
                      background: 'transparent',
                      border: 0,
                      color: active ? 'var(--hj-ink)' : 'var(--hj-ink-3)',
                      borderBottom: `2px solid ${active ? 'var(--hj-blue)' : 'transparent'}`,
                      marginBottom: -1,
                      fontSize: 13,
                      fontWeight: 500,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <t.Icon className="w-3.5 h-3.5" />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>

            {bgPickerTab === 'saved' && userAssets.length > 0 && (
              <>
                {loadingAssets ? (
                  <div className="flex items-center justify-center" style={{ padding: '24px 0', color: 'var(--hj-ink-3)' }}>
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
                    {userAssets.map((asset, i) => {
                      const assetUrl = asset.url.startsWith('/api/s3/')
                        ? `${window.location.origin}${asset.url}`
                        : asset.url;
                      const active = bgReplacementUrl === assetUrl;
                      return (
                        <button
                          key={asset._id || i}
                          type="button"
                          onClick={() => {
                            setBgReplacementUrl(assetUrl);
                            setBgReplacementType(asset.type === 'video' ? 'video' : 'image');
                          }}
                          className="group relative aspect-video overflow-hidden"
                          style={{
                            padding: 4,
                            borderRadius: 'var(--hj-r-sm)',
                            background: 'var(--hj-surface-2)',
                            border: `1.5px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                            cursor: 'pointer',
                          }}
                        >
                          {asset.type === 'video' ? (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{ background: 'oklch(0.12 0.005 70)', borderRadius: 4 }}
                            >
                              <Video className="w-5 h-5" style={{ color: 'var(--hj-ink-3)' }} />
                            </div>
                          ) : (
                            <img
                              src={asset.url}
                              alt={asset.prompt || 'Background'}
                              className="w-full h-full object-cover"
                              style={{ borderRadius: 4 }}
                            />
                          )}
                          {asset.prompt && (
                            <div
                              className="absolute left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{
                                bottom: 4,
                                padding: '3px 6px',
                                background: 'oklch(0 0 0 / 0.7)',
                                borderRadius: '0 0 4px 4px',
                              }}
                            >
                              <p
                                className="truncate"
                                style={{ fontSize: 10, color: 'white', margin: 0 }}
                              >
                                {asset.prompt}
                              </p>
                            </div>
                          )}
                          {active && (
                            <div
                              className="absolute flex items-center justify-center"
                              style={{
                                top: 8,
                                right: 8,
                                width: 18,
                                height: 18,
                                borderRadius: 999,
                                background: 'var(--hj-blue)',
                                color: 'oklch(0.18 0.005 70)',
                              }}
                            >
                              <CheckCircle className="w-3 h-3" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {bgPickerTab === 'generate' && (
              <div className="flex flex-col" style={{ gap: 14 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <div className="mono-label" style={{ marginBottom: 6 }}>Type</div>
                    <SegmentedControl
                      options={[
                        { value: 'image', label: 'Image' },
                        { value: 'video', label: 'Video' },
                      ]}
                      active={bgGenType}
                      onChange={(v) => setBgGenType(v as 'image' | 'video')}
                    />
                  </div>
                  <div>
                    <div className="mono-label" style={{ marginBottom: 6 }}>Aspect ratio</div>
                    <SegmentedControl
                      options={[
                        { value: '16:9', label: '16:9' },
                        { value: '9:16', label: '9:16' },
                        { value: '1:1',  label: '1:1'  },
                        { value: '4:3',  label: '4:3'  },
                      ]}
                      active={bgGenAspectRatio || '16:9'}
                      onChange={setBgGenAspectRatio}
                    />
                  </div>
                </div>
                <label className="flex flex-col gap-1.5">
                  <span className="mono-label">Describe your background</span>
                  <Textarea
                    value={bgGenPrompt}
                    onChange={(e) => setBgGenPrompt(e.target.value)}
                    placeholder="e.g. A clean, modern apartment living room with soft natural light"
                    rows={3}
                  />
                </label>
                <button
                  type="button"
                  onClick={generateBackground}
                  disabled={bgGenerating}
                  className="self-start inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
                  style={{
                    padding: '9px 16px',
                    borderRadius: 999,
                    background: 'var(--hj-blue)',
                    border: '1px solid var(--hj-blue)',
                    color: 'oklch(0.18 0.005 70)',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    cursor: bgGenerating ? 'not-allowed' : 'pointer',
                    opacity: bgGenerating ? 0.6 : 1,
                    minHeight: 44,
                  }}
                >
                  {bgGenerating ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5" /> Generate background</>
                  )}
                </button>
              </div>
            )}

            {bgPickerTab === 'upload' && (
              <div
                {...bgFileDropzone.getRootProps()}
                className="text-center cursor-pointer transition-colors"
                style={{
                  padding: '32px 16px',
                  borderRadius: 'var(--hj-r-md)',
                  border: `1.5px dashed ${bgFileDropzone.isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                  background: bgFileDropzone.isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                }}
              >
                <input {...bgFileDropzone.getInputProps()} />
                {bgUploadingFile ? (
                  <Loader2 className="w-7 h-7 mx-auto animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
                ) : (
                  <>
                    <ImagePlus
                      className="mx-auto"
                      size={28}
                      strokeWidth={1.6}
                      style={{ color: 'var(--hj-ink-3)' }}
                    />
                    <p
                      className="m-0"
                      style={{ marginTop: 10, fontSize: 13.5, color: 'var(--hj-ink-2)' }}
                    >
                      Drop an image or video, or click to browse
                    </p>
                  </>
                )}
              </div>
            )}
          </section>

          {/* Selected backdrop preview */}
          {bgReplacementUrl && (
            <section>
              <div className="mono-label" style={{ marginBottom: 8 }}>Selected backdrop</div>
              <div
                className="relative inline-block overflow-hidden"
                style={{
                  borderRadius: 'var(--hj-r-sm)',
                  border: '1px solid var(--hj-hairline)',
                  background: 'oklch(0.1 0.005 70)',
                }}
              >
                {bgReplacementType === 'preset' ? (
                  <div
                    style={{
                      height: 120,
                      width: 200,
                      background: bgReplacementUrl.replace('solid:', ''),
                    }}
                  />
                ) : bgReplacementType === 'video' ? (
                  <video
                    src={bgReplacementUrl}
                    className="object-cover"
                    autoPlay
                    muted
                    loop
                    style={{ height: 120 }}
                  />
                ) : (
                  <img
                    src={bgReplacementUrl}
                    alt="Background"
                    className="object-cover"
                    style={{ height: 120 }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setBgReplacementUrl('')}
                  aria-label="Remove backdrop"
                  className="absolute flex items-center justify-center"
                  style={{
                    top: 6,
                    right: 6,
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    background: 'oklch(0 0 0 / 0.6)',
                    border: 0,
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </section>
          )}

          {/* Video backdrop loop options */}
          {bgReplacementUrl &&
            bgReplacementType === 'video' &&
            bgVideoDuration !== null &&
            video !== null &&
            bgVideoDuration < video.duration && (
              <section
                style={{
                  padding: 14,
                  borderRadius: 'var(--hj-r-sm)',
                  border: '1px solid var(--hj-yellow)',
                  background: 'var(--hj-yellow-tint)',
                }}
              >
                <p
                  style={{
                    margin: '0 0 10px',
                    fontSize: 12.5,
                    color: 'var(--hj-ink-2)',
                  }}
                >
                  Video backdrop ({Math.round(bgVideoDuration)}s) is shorter than your video ({Math.round(video.duration)}s).
                </p>
                <div style={{ maxWidth: 340 }}>
                  <SegmentedControl
                    options={[
                      { value: 'loop', label: 'Loop to match' },
                      { value: 'stop', label: 'Stop at end' },
                    ]}
                    active={bgVideoLoop ? 'loop' : 'stop'}
                    onChange={(v) => setBgVideoLoop(v === 'loop')}
                  />
                </div>
              </section>
            )}
        </div>
      )}
    </StepCard>
  );

  const renderProcessStep = () => {
    const stageLabel = processStatus === 'removing'
      ? 'Removing background'
      : processStatus === 'compositing'
        ? 'Compositing'
        : processStatus === 'completed'
          ? 'Done'
          : 'Processing';

    return (
      <StepCard>
        <StepHeader
          kicker="Step 03 · Process"
          title="Joe's working."
          subtitle={bgMode === 'greenscreen'
            ? 'Cutting the subject and laying down a green matte. Audio is preserved.'
            : 'Cutting the subject, then compositing your new backdrop behind it.'}
        />

        {/* Settings summary */}
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
            marginBottom: 16,
          }}
        >
          <div className="mono-label" style={{ marginBottom: 8 }}>Final video</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {[
              { label: 'Source', value: video?.name || '—' },
              { label: 'Mode', value: bgMode === 'greenscreen' ? 'Green-screen matte' : 'Replaced backdrop' },
              ...(bgMode === 'replace'
                ? [{
                    label: 'Backdrop',
                    value: bgReplacementType === 'preset'
                      ? 'Solid color'
                      : bgReplacementType === 'video'
                        ? `Video${bgVideoLoop ? ' · looped' : ''}`
                        : 'Image',
                  }]
                : []),
              { label: 'Length', value: video?.duration ? `${Math.round(video.duration)}s` : '—' },
            ].map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-3">
                <span style={{ fontSize: 12, color: 'var(--hj-ink-4)' }}>{row.label}</span>
                <span
                  className="truncate text-right"
                  style={{ fontSize: 13, color: 'var(--hj-ink-2)', fontWeight: 500 }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* In-flight progress card */}
        {processInFlight && (
          <div
            style={{
              padding: '20px 22px',
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
              marginBottom: 14,
            }}
          >
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="rounded-full"
                  style={{
                    width: 10,
                    height: 10,
                    background: 'var(--hj-blue)',
                    boxShadow: '0 0 12px var(--hj-blue)',
                    animation: 'pulse 1.4s ease-in-out infinite',
                  }}
                />
                <span style={{ fontSize: 14.5, color: 'var(--hj-ink)', fontWeight: 500 }}>
                  {stageLabel}…
                </span>
              </div>
              <span
                className="tabular"
                style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 14, color: 'var(--hj-ink-2)' }}
              >
                {Math.min(100, Math.round(processProgress))}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 999,
                background: 'var(--hj-surface-3)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, processProgress)}%`,
                  background: 'var(--hj-blue)',
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
            <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
              <span className="mono-label">Don't close the tab</span>
              {processMessage && (
                <span className="mono-label">{processMessage}</span>
              )}
            </div>
            <p
              style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: '1px solid var(--hj-hairline-soft)',
                fontSize: 12,
                color: 'var(--hj-ink-2)',
                lineHeight: 1.5,
              }}
            >
              Background removal usually takes a few minutes depending on clip length.
              {bgMode === 'replace' && ' The composite render kicks off automatically when the matte is ready.'}
            </p>
          </div>
        )}

        {processStatus === 'failed' && (
          <div
            style={{
              padding: 16,
              borderRadius: 'var(--hj-r-sm)',
              border: '1px solid hsl(var(--destructive) / 0.5)',
              background: 'hsl(var(--destructive) / 0.08)',
            }}
          >
            <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
              <AlertCircle className="w-5 h-5" style={{ color: 'hsl(var(--destructive))' }} />
              <p style={{ fontWeight: 600, color: 'hsl(var(--destructive))', margin: 0 }}>Processing failed</p>
            </div>
            {processError && (
              <p style={{ fontSize: 13, color: 'hsl(var(--destructive) / 0.85)', margin: 0, lineHeight: 1.4 }}>
                {processError}
              </p>
            )}
          </div>
        )}

        {processStatus === 'completed' && (
          <div
            className="flex items-center gap-3"
            style={{
              padding: '14px 16px',
              borderRadius: 'var(--hj-r-sm)',
              background: 'oklch(0.42 0.15 145 / 0.12)',
              border: '1px solid var(--hj-green)',
              color: 'var(--hj-ink)',
            }}
          >
            <CheckCircle className="w-5 h-5" style={{ color: 'var(--hj-green)' }} />
            <div>
              <div style={{ fontWeight: 600 }}>Done</div>
              <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2 }}>
                Continue to download your video.
              </div>
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const renderDownloadStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 04 · Download"
        title="Room's gone."
        subtitle={bgMode === 'greenscreen'
          ? 'Your green-screen export is ready — key it out anywhere.'
          : 'Your video with the new backdrop is ready.'}
      />

      {finalVideoUrl && (
        <>
          {/* Big preview tile */}
          <div
            className="aspect-video overflow-hidden relative"
            style={{
              background: 'oklch(0.08 0.005 70)',
              borderRadius: 'var(--hj-r-md)',
              border: '1px solid var(--hj-hairline)',
              marginBottom: 16,
            }}
          >
            <video
              src={finalVideoUrl}
              className="w-full h-full object-contain"
              controls
              preload="auto"
              playsInline
            />
          </div>

          {/* Primary action row */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center justify-center gap-2 transition-opacity"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 15,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <Download className="w-4 h-4" />
              Save to device
            </button>
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center justify-center gap-2"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <RotateCcw className="w-4 h-4" />
              Start over
            </button>
          </div>
        </>
      )}
    </StepCard>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload': return renderUploadStep();
      case 'background': return renderBackgroundStep();
      case 'process': return renderProcessStep();
      case 'download': return renderDownloadStep();
    }
  };

  // Per-step shell-level Next behavior — the toolbar and MobileActionBar Next
  // buttons do the same thing as the step's primary action.
  const currentStepAdvance: {
    onAdvance: () => void;
    label?: string;
    disabled?: boolean;
    showAdvance?: boolean;
  } = (() => {
    switch (currentStep) {
      case 'upload':
        return {
          onAdvance: advanceStep,
          label: video?.status === 'uploading'
            ? 'Uploading…'
            : video?.status === 'converting'
              ? 'Converting…'
              : 'Continue to Background',
          disabled: !videoReady,
        };
      case 'background':
        return {
          onAdvance: handleBackgroundAdvance,
          label: bgMode === 'greenscreen' ? 'Export green-screen' : 'Remove background',
          disabled: bgMode === 'replace' && !bgReplacementUrl,
        };
      case 'process': {
        if (processStatus === 'failed') {
          return { onAdvance: () => runPipeline(), label: 'Try again' };
        }
        if (processStatus === 'completed') {
          return { onAdvance: advanceStep, label: 'Continue to Download' };
        }
        return {
          onAdvance: () => {},
          label: processStatus === 'compositing' ? 'Compositing…' : 'Removing background…',
          disabled: true,
        };
      }
      case 'download':
        // Terminal step — workspace owns its actions (Save to Device, Start over).
        return { onAdvance: () => {}, showAdvance: false };
      default:
        return { onAdvance: advanceStep };
    }
  })();

  // Shell-level Back: walk to the previous step via goToStep (which resets
  // downstream state).
  const onShellBack = () => {
    if (stepIndex > 0) goToStep(STEP_ORDER[stepIndex - 1]);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--hj-bg)' }}>
      <ToolShell
        toolName="Background Remover"
        toolNumber={`Tool · ${String(stepIndex + 1).padStart(2, '0')} / ${String(STEPS.length).padStart(2, '0')}`}
        usesCredits
        steps={STEPS}
        currentStepKey={currentStep}
        completedKeys={completedSteps as Set<string>}
        onStepChange={(k) => goToStep(k as WizardStep)}
        onBack={onShellBack}
        onAdvance={currentStepAdvance.onAdvance}
        advanceLabel={currentStepAdvance.label}
        advanceDisabled={currentStepAdvance.disabled}
        showAdvance={currentStepAdvance.showAdvance !== false}
        onBackToStudio={() => navigate('/')}
      >
        {renderCurrentStep()}
      </ToolShell>

      <CreditConfirmDialog />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} trigger="ai_feature" />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <LibraryPicker open={showLibraryPicker} onClose={() => setShowLibraryPicker(false)} onPick={handleLibraryPick} />
    </div>
  );
};
